import { useSyncExternalStore } from 'react'
import type { Subscription } from '@rails/actioncable'
import api from '../lib/api'
import { sharedConsumer } from '../lib/cable'
import type { AppNotification } from '../types/notification'

// 铃铛的那点状态，放在组件外面（形状照 Agent/launcherStore）。
//
// **为什么不是组件内的 useState**：Header 同时渲染两个 <NotificationBell />
// （宽屏一个、窄屏一个，靠 CSS 各藏一个，但两个都挂着）。状态留在组件里就是
// 两份，而且会开两条 WebSocket —— 红点还可能一个亮一个不亮。
//
// unread 为 null = 还没有人告诉过我们；页面加载时由共享 props 播种
// （Web::BaseController 的 unread_notifications），之后由 cable 推的值接管。
type NotificationState = {
  unread: number
  rows: AppNotification[] | null
  loading: boolean
}

let state: NotificationState = { unread: 0, rows: null, loading: false }
const listeners = new Set<() => void>()

function update(next: Partial<NotificationState>) {
  state = { ...state, ...next }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useNotifications(): NotificationState {
  return useSyncExternalStore(subscribe, () => state)
}

// 整页加载 / Inertia 导航带来的服务端真值。cable 推过更新的之后这个值可能
// 偏旧，但每次导航拿到的都是那一刻重新数的，所以直接覆盖就行。
export function seedUnread(unread: number) {
  if (state.unread !== unread) update({ unread })
}

export function loadNotifications() {
  update({ loading: true })

  return api.notifications
    .list()
    .then((data) => {
      const payload = data as { notifications: AppNotification[]; unread: number }
      update({ rows: payload.notifications ?? [], unread: payload.unread ?? 0 })
    })
    .catch(() => update({ rows: [] }))
    .finally(() => update({ loading: false }))
}

export function markRead(id: number) {
  const row = state.rows?.find((item) => item.id === id)
  if (row && !row.read) {
    update({
      unread: Math.max(0, state.unread - 1),
      rows: state.rows?.map((item) => (item.id === id ? { ...item, read: true } : item)) ?? null,
    })
  }

  // 标不上也照样跳过去：人已经看到这条通知了，卡在这里没有意义。服务端那边
  // 成功之后会推一次新的未读数，本地这次先减是为了红点当场就变。
  return api.notifications.read(id).catch(() => undefined)
}

export function markAllRead() {
  update({ unread: 0, rows: state.rows?.map((item) => ({ ...item, read: true })) ?? null })

  return api.notifications.readAll().catch(() => undefined)
}

// cable 推过来的一条：{ unread, notification }。notification 为空就是只有
// 未读数变了（别处标了已读）。
function receive(data: { unread?: number; notification?: AppNotification | null }) {
  const next: Partial<NotificationState> = {}
  if (typeof data.unread === 'number') next.unread = data.unread

  // 列表还没取过就不插：下次点开会整份取回来，这时插一条反而会变成「只有
  // 一条」的假列表。取过了才插到最前面，并且按 id 去重 —— 推送和点开时的
  // 重取可能撞在一起。
  const incoming = data.notification
  if (incoming && state.rows && !state.rows.some((row) => row.id === incoming.id)) {
    next.rows = [incoming, ...state.rows]
  }

  update(next)
}

let subscription: Subscription | null = null

// 一条连接，开了就不收 —— Header 活得和这个标签页一样长，而断开重连要付
// 一次握手。只在登录用户第一次渲染铃铛时才建（未登录的页面上铃铛不渲染，
// 所以游客不会开 WebSocket）。
export function ensureNotificationStream() {
  if (subscription) return

  subscription = sharedConsumer().subscriptions.create(
    { channel: 'NotificationsChannel' },
    {
      received: (data: { unread?: number; notification?: AppNotification | null }) => receive(data),
      rejected: () => console.error('Connection to NotificationsChannel was rejected'),
    },
  )
}
